import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGameStore } from '../store/gameStore'
import type { OracleQuote } from '../types'
import { drawQuote } from '../data/oracleLoader'
import Icon from '../components/Common/Icon'

type ViewMode = 'draw' | 'favorites' | 'history'

interface Particle {
  id: number
  x: number
  y: number
  tx: number
  ty: number
  size: number
  color: string
}

function OraclePage() {
  const {
    oracleQuotes,
    oracleStorage,
    loadOracleQuotes,
    recordOracleDraw,
    toggleOracleFavorite,
    isInitialized,
    initialize,
  } = useGameStore()

  const [currentQuote, setCurrentQuote] = useState<OracleQuote | null>(null)
  const [isFlipped, setIsFlipped] = useState(false)
  const [mode, setMode] = useState<ViewMode>('draw')
  const [particles, setParticles] = useState<Particle[]>([])
  const [isShaking, setIsShaking] = useState(false)
  const [showGlow, setShowGlow] = useState(false)
  const [isRevealing, setIsRevealing] = useState(false)
  const [cooldownLeft, setCooldownLeft] = useState(0)

  useEffect(() => {
    if (!isInitialized) initialize()
    if (oracleQuotes.length === 0) loadOracleQuotes()
  }, [isInitialized, oracleQuotes.length, initialize, loadOracleQuotes])

  // 冷却时间倒计时
  useEffect(() => {
    if (cooldownLeft <= 0) return
    const timer = setInterval(() => {
      setCooldownLeft((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldownLeft])

  // 触发像素爆炸粒子效果
  const triggerBurst = (originX = 50, originY = 50) => {
    const colors = [
      'var(--color-gold)',
      'var(--color-bull)',
      'var(--color-cyan)',
      'var(--color-border-bright)',
      '#ffffff',
    ]
    const newParticles: Particle[] = []
    const particleCount = 24

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4
      const distance = 40 + Math.random() * 70
      newParticles.push({
        id: Date.now() + i + Math.random(),
        x: originX,
        y: originY,
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance,
        size: Math.floor(Math.random() * 6) + 4, // 4px ~ 10px 像素块
        color: colors[Math.floor(Math.random() * colors.length)] || 'var(--color-gold)',
      })
    }

    setParticles(newParticles)
    setShowGlow(true)
    setTimeout(() => {
      setParticles([])
      setShowGlow(false)
    }, 650)
  }

  const handleDraw = () => {
    if (oracleQuotes.length === 0 || isRevealing || cooldownLeft > 0) return

    setIsRevealing(true)
    setIsShaking(true)

    // 光带环绕扫过一圈
    setTimeout(() => {
      setIsShaking(false)
      const recent = oracleStorage.drawnIds.slice(-Math.floor(oracleQuotes.length * 0.8))
      const quote = drawQuote(oracleQuotes, recent)
      setCurrentQuote(quote)
      setIsFlipped(false)
      recordOracleDraw(quote.id)
      setIsRevealing(false)
    }, 450)
  }

  const handleReveal = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isRevealing) return

    setIsRevealing(true)
    setIsShaking(true)

    const rect = e.currentTarget.getBoundingClientRect()
    const originX = rect.left + rect.width / 2
    const originY = rect.top - 60

    // 光带绕四周跑完一圈后，爆破粒子并翻转牌面
    setTimeout(() => {
      setIsShaking(false)
      triggerBurst(originX, originY)
      setIsFlipped(true)
      setIsRevealing(false)
      setCooldownLeft(5)
    }, 450)
  }

  const handleFavorite = () => {
    if (!currentQuote) return
    toggleOracleFavorite(currentQuote.id)
  }

  const isCurrentFavorited = currentQuote
    ? oracleStorage.favorites.includes(currentQuote.id)
    : false

  const favoriteQuotes = oracleQuotes.filter(q =>
    oracleStorage.favorites.includes(q.id)
  )
  const historyQuotes = oracleQuotes.filter(q =>
    oracleStorage.drawnIds.includes(q.id)
  )

  return (
    <div className="min-h-screen p-4 md:p-6 crt-scanlines">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-3 inline-block"
        >
          ◀ 返回首页
        </Link>

        {/* ========== 标题 ========== */}
        <div className="pixel-box pixel-box-bright p-4 mb-4 text-center">
          <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
            ► 答案之书
          </div>
          <h1 className="font-pixel text-xl text-[color:var(--color-text-bright)] mb-1">
            Z 哥语录
          </h1>
          <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)]">
            ► 翻开命运，{oracleQuotes.length} 句经典随机出现
          </p>
        </div>

        {/* ========== 模式切换 ========== */}
        <div className="flex gap-2 mb-4">
          {(['draw', 'favorites', 'history'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={
                'pixel-button flex-1 ' +
                (mode === m ? 'pixel-button-primary' : '')
              }
            >
              {m === 'draw' && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon name="scroll" size={12} />
                  抽卡
                </span>
              )}
              {m === 'favorites' && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon name="star" size={12} />
                  收藏 ({oracleStorage.favorites.length})
                </span>
              )}
              {m === 'history' && `↻ 历史 (${oracleStorage.drawnIds.length})`}
            </button>
          ))}
        </div>

        {/* ========== 抽卡模式 ========== */}
        {mode === 'draw' && (
          <>
            {/* 翻牌容器 */}
            <div
              className={
                'pixel-box pixel-box-bright p-6 mb-4 min-h-[280px] flex flex-col items-center justify-center relative overflow-hidden transition-all ' +
                (isShaking ? 'oracle-shake-anim ' : '') +
                (showGlow ? 'oracle-glow-card ' : '')
              }
            >
              {/* 四周流转的聚能光带（Loading / 翻牌揭晓中） */}
              {isRevealing && (
                <div className="oracle-border-orbit">
                  <div className="beam-t" />
                  <div className="beam-r" />
                  <div className="beam-b" />
                  <div className="beam-l" />
                </div>
              )}

              {/* 爆炸像素粒子 */}
              {particles.map((p) => (
                <div
                  key={p.id}
                  className="pixel-burst-particle"
                  style={{
                    left: `${p.x}px`,
                    top: `${p.y}px`,
                    width: `${p.size}px`,
                    height: `${p.size}px`,
                    backgroundColor: p.color,
                    '--tw-particle-x': `${p.tx}px`,
                    '--tw-particle-y': `${p.ty}px`,
                  } as React.CSSProperties}
                />
              ))}

              {!currentQuote ? (
                <div className="text-center">
                  <div className="mb-3 pixel-blink text-[color:var(--color-gold)]">
                    <Icon name="scroll" size={56} />
                  </div>
                  <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)]">
                    点下方按钮开启答案
                  </p>
                </div>
              ) : (
                <div
                  className="relative w-full"
                  style={{
                    perspective: '1000px',
                  }}
                >
                  <div
                    className="transition-transform"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                      // 正向翻开 700ms 平滑动画，反向重置（再抽一次）瞬时 0ms，避免动画中暴露内容
                      transitionDuration: isFlipped ? '700ms' : '0ms',
                    }}
                  >
                    {/* 正面 - 谜面 */}
                    <div
                      style={{ backfaceVisibility: 'hidden' }}
                      className="flex flex-col items-center justify-center min-h-[220px]"
                    >
                      <div className="text-5xl mb-3">?</div>
                      <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)] mb-2">
                        命运之签
                      </p>
                      <p className="font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
                        点击下方「翻开答案」
                      </p>
                    </div>

                    {/* 背面 - 语录 */}
                    <div
                      style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                      }}
                      className="flex flex-col items-center justify-center min-h-[220px] px-4"
                    >
                      <div className="font-pixel text-[9px] text-[color:var(--color-gold)] mb-3 tracking-wider">
                        ► {currentQuote.theme}
                      </div>
                      <p className="font-pixel text-base text-[color:var(--color-text-bright)] text-center leading-relaxed mb-4">
                        「{currentQuote.quote}」
                      </p>
                      <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
                        — {currentQuote.sourceRef}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 像素风格冷却倒计时反馈 */}
            {cooldownLeft > 0 && (
              <div className="mb-3 p-2.5 bg-[color:var(--color-bg-elevated)] border-2 border-[color:var(--color-gold)] flex flex-col items-center justify-center gap-1.5 font-pixel-display animate-pulse">
                <div className="flex items-center gap-2 text-xs text-[color:var(--color-gold)]">
                  <span className="pixel-blink">⏱️</span>
                  <span>天机凝神中... 再次求卜需等待 {cooldownLeft} 秒</span>
                </div>
                {/* 像素充能进度条 */}
                <div className="w-full h-2 bg-black/40 border border-[color:var(--color-border-bright)] relative overflow-hidden">
                  <div
                    className="h-full bg-[color:var(--color-gold)] transition-all duration-1000 linear"
                    style={{ width: `${((5 - cooldownLeft) / 5) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            {currentQuote && !isFlipped ? (
              <button
                onClick={handleReveal}
                disabled={isRevealing || cooldownLeft > 0}
                className="pixel-button pixel-button-primary w-full mb-3"
              >
                {isRevealing
                  ? '✨ 天机汇聚中...'
                  : cooldownLeft > 0
                  ? `⏱️ 凝神冷却中 (${cooldownLeft}s)`
                  : '► 翻开答案'}
              </button>
            ) : currentQuote && isFlipped ? (
              <div className="flex gap-2 mb-3">
                <button
                  onClick={handleFavorite}
                  disabled={isRevealing}
                  className={
                    'pixel-button flex-1 ' +
                    (isCurrentFavorited ? 'pixel-button-primary' : '')
                  }
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name={isCurrentFavorited ? 'star' : 'starEmpty'} size={12} />
                    {isCurrentFavorited ? '已收藏' : '收藏'}
                  </span>
                </button>
                <button
                  onClick={handleDraw}
                  disabled={isRevealing || cooldownLeft > 0}
                  className="pixel-button pixel-button-primary flex-1"
                >
                  {isRevealing
                    ? '⚡ 凝神中...'
                    : cooldownLeft > 0
                    ? `⏱️ 冷却中 (${cooldownLeft}s)`
                    : '► 再抽一张'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleDraw}
                disabled={oracleQuotes.length === 0 || isRevealing || cooldownLeft > 0}
                className="pixel-button pixel-button-primary w-full mb-3"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Icon name="scroll" size={12} />
                  {isRevealing
                    ? '⚡ 虔诚求卜中...'
                    : cooldownLeft > 0
                    ? `⏱️ 凝神冷却 (${cooldownLeft}s)`
                    : '开启答案'}
                </span>
              </button>
            )}

            {/* 底部提示 */}
            <div className="text-center font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
              {oracleQuotes.length === 0
                ? '► 加载语录中...'
                : `► 共 ${oracleQuotes.length} 句，已抽 ${oracleStorage.drawnIds.length} 次`}
            </div>
          </>
        )}

        {/* ========== 收藏模式 ========== */}
        {mode === 'favorites' && (
          <QuoteList
            quotes={favoriteQuotes}
            emptyText="还没有收藏语录。抽到喜欢的点「收藏」。"
            favorites={oracleStorage.favorites}
            onToggle={toggleOracleFavorite}
          />
        )}

        {/* ========== 历史模式 ========== */}
        {mode === 'history' && (
          <QuoteList
            quotes={historyQuotes}
            emptyText="还没有抽过。点「抽卡」开始。"
            favorites={oracleStorage.favorites}
            onToggle={toggleOracleFavorite}
          />
        )}
      </div>
    </div>
  )
}

function QuoteList({
  quotes,
  emptyText,
  favorites,
  onToggle,
}: {
  quotes: OracleQuote[]
  emptyText: string
  favorites: string[]
  onToggle: (id: string) => void
}) {
  if (quotes.length === 0) {
    return (
      <div className="pixel-box p-8 text-center">
        <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)]">
          {emptyText}
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {quotes.map((q) => {
        const isFav = favorites.includes(q.id)
        return (
          <div key={q.id} className="pixel-box p-3">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="font-pixel text-[9px] text-[color:var(--color-gold)] mb-1 tracking-wider">
                  ► {q.theme}
                </div>
                <p className="font-pixel text-sm text-[color:var(--color-text-bright)] leading-snug mb-2">
                  「{q.quote}」
                </p>
                <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
                  — {q.sourceRef}
                </div>
              </div>
              <button
                onClick={() => onToggle(q.id)}
                className={
                  'pixel-button text-xs flex-shrink-0 ' +
                  (isFav ? 'pixel-button-primary' : '')
                }
                title={isFav ? '取消收藏' : '收藏'}
              >
                <Icon name={isFav ? 'star' : 'starEmpty'} size={12} />
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default OraclePage